import { MouseEvent, useEffect, useState, Dispatch, SetStateAction } from "react"
import PopupHeader from "../../../components/Popups/PopupHeader"
import PopupSideNav from "../../../components/Popups/PopupSideNav"
import PopupFooter from "../../../components/Popups/PopupFooter"
import { Btn } from "../../../components/Buttons/Buttons";
import SetPredictionParameters from "./RunPredictionComponents/SetPredictionParameters";
import { ACPredictDAGRequestModel, Dataset, ModelFamily } from "../../../api/models";
import SuccessMessageModal from "../../../components/Popups/SuccessMessageModal";
import { classificationMethods, regressionMethods } from "../../../utils/supplementalData";
import usePost from "../../../hooks/endpoints/usePost";
import ModalBase from "../../../components/Popups/ModalBase";
import { SelectListOption } from "../../../components/Filtering/PaginatedSelectList";
import SelectModelsV2 from "./RunPredictionComponents/SelectModelsV2";
import SelectDatasetV2 from "./RunPredictionComponents/SelectDatasetV2";
import { customInstance } from "../../../api/custom-instance";

const navItems = ['Select Dataset', 'Select Models', 'Set Prediction Parameters'];

const defaultForm: ACPredictDAGRequestModel = {
    name: '',
    dataset: {
        dataset_hash: '',
        structure_col: '',
        y_col: '',
    },
    model_family_id_list: {},
    tag_behavior: 'define',
    tags: [],
    prediction_type: 'normal',
    perform_evaluation: false,
    molecule_highlighting: false,
    explainability_enabled: false,
};

const getStructureCol = (data: Dataset) => {
    const columnMetadata = data.dataset_metadata!.column_metadata;
    if (columnMetadata) {
        let structureKey = '';
        
        const structure = Object.keys(columnMetadata).find(key => key.includes('structure') || key.includes('smile'));

        if (structure) {
            structureKey = structure
        } else {
            Object.keys(columnMetadata).forEach(columnKey => {
                const columnVal = columnMetadata[columnKey];
                if (columnVal && columnVal.data_type && columnVal.data_type === 'structure') {
                    structureKey = columnKey
                }
            })
        }

        return structureKey;
    } else {
        return ''
    }
}

export default function BatchPredictionModal({
    selectedDatasetId,
    selectedModelFamilyIds,
    setPopupMessage,
    handleCloseMenu,
    isPrimaryMenu,
    cancelFn,
    isHidden=false,
    open: openProp,
    setOpen: setOpenProp,
}: {
    selectedDatasetId?: string,
    selectedModelFamilyIds?: number[],
    setPopupMessage : (popupMessage: string, isSuccessMessage: boolean) => void,
    handleCloseMenu?: () => void,
    cancelFn?: () => void,
    isPrimaryMenu: boolean,
    isHidden?: boolean,
    open?: boolean,
    setOpen?: Dispatch<SetStateAction<boolean>>,
}) {
    const [openBackup, setOpenBackup] = useState(false);
    // Use prop if provided, otherwise fallback to internal state
    const open = openProp !== undefined ? openProp : openBackup;
    const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenBackup;

    const handleOpen = () => setOpen(true);
    const [ showSelect, setShowSelect ] = useState(false);

    const [ btns, setBtns ] = useState<Btn[]>([]);
    const [ selectedNavIndex, setSelectedNavIndex ] = useState(0);
    const [ form, setForm ] = useState<ACPredictDAGRequestModel>(defaultForm);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isDisabled, setIsDisabled ] = useState(false);
    const [ isSuccess, setIsSuccess ] = useState(false);
    
    const [ modelTypes, setModelTypes ] = useState<string[]>(classificationMethods.concat(regressionMethods));
    const [ selectedDatasets, setSelectedDatasets ] = useState<Dataset[]>([]);
    const [ selectedModels, setSelectedModels ] = useState<ModelFamily[]>([]);
    const [ isPrefetching, setIsPrefetching ] = useState(false);

    const handleClose = () => {
        setOpen(false);
        resetValues();
        setShowSelect(false);
    };

    const resetValues = () => {
        setSelectedNavIndex(0);
        setSelectedDatasets([]);
        setSelectedModels([]);
        setForm(defaultForm);
        setIsDisabled(false);
        setIsLoading(false);
        setIsSuccess(false);

        cancelFn && cancelFn();
        handleCloseMenu && setTimeout(() => handleCloseMenu(), 0);
    };

    const { handleBatchPrediction } = usePost({
        setPopupMessage,
        setIsDisabled,
        setIsLoading,
        setIsSuccess,
    });


    useEffect(() => {
        const fetchDatasetOrModels = async (id: string | number[]) => {
            setIsPrefetching(true);
            try {
                if (typeof id === 'string') {
                    const response = await customInstance({
                        url: `dataset/?dataset_hash=${id}&include_dataset_metadata=true&include_airflow_metadata=false`,
                        method: 'GET',
                    });
                    const data = response as unknown as Dataset;
                    setSelectedDatasets([data]);
                } else {
                    const models: ModelFamily[] = [];

                    const promises = id.map(async (mfId) => {
                        const response = await customInstance({
                            url: `model_family/?model_family_id=${mfId}&include_model_family_metadata=true&include_model_stats=true&include_airflow_metadata=false`,
                            method: 'GET',
                        });
                        const data = response as unknown as ModelFamily;
                        models.push(data);
                    });

                    await Promise.all(promises);

                    setSelectedModels(models);
                }
            } catch (err:any) {
                console.log(err);
            }
            setIsPrefetching(false);
        }


        if (selectedDatasetId) {
            setSelectedNavIndex(1);
            fetchDatasetOrModels(selectedDatasetId);
        } else if (selectedModelFamilyIds) {
            setSelectedNavIndex(0);
            fetchDatasetOrModels(selectedModelFamilyIds);
        }
    }, [selectedDatasetId, selectedModelFamilyIds]);

    useEffect(() => {
        if (selectedDatasets.length > 0 && selectedModels.length > 0) {
            const tags: string[] = [];

            const selectedDataset = selectedDatasets[0];
            const name = selectedDataset.name ?? '';

            selectedModels.forEach(model => {
                const modelTags = model.model_family_metadata!.tags as string[] ?? [];
                modelTags.forEach(tag => {
                    const tagValue = tag ?? 'null';
                    if (!tags.includes(tagValue)) {
                        tags.push(tagValue);
                    }
                })
            });

            const dsTags = selectedDataset.file_metadata && selectedDataset.file_metadata.tags ? selectedDataset.file_metadata.tags as string[] : [];
            dsTags.forEach(tag => {
                const tagValue = tag ?? 'null';
                if (!tags.includes(tagValue)) {
                    tags.push(tagValue);
                }
            });

            const structure_col = getStructureCol(selectedDataset);

            setForm((prevForm) => ({
                ...prevForm,
                name,
                tags,
                dataset: {
                    dataset_hash: selectedDataset.file_hash!,
                    structure_col,
                    y_col: '',
                }
            }));
        }
    }, [selectedDatasets, selectedModels]);


    useEffect(() => {
        if (selectedModels.length === 0 || selectedDatasets.length === 0 || !form.name || !form.dataset!.dataset_hash) {
            setIsDisabled(true);
        } else if (form.perform_evaluation && !form.dataset!.y_col) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [selectedDatasets.length, selectedModels.length, form]);


    useEffect(() => {
        const chosenModels: SelectListOption[] = selectedModels.length > 0 ? selectedModels.map(model => ({ id: model.id!, name: model.name ?? '' })) : [];

        setBtns([
            {
                type: "tertiary",
                styling: "outline",
                id: 'cancel-batch-prediction',
                label: 'Cancel',
                action: (e: MouseEvent<HTMLButtonElement>) => {handleClose()},
            },
            {
                type: isDisabled ? 'disabled' : 'primary',
                styling: "solid",
                id: 'run-batch-prediction-btn',
                label: "Run",
                action: async (e: MouseEvent<HTMLButtonElement>) => {handleBatchPrediction(e, chosenModels, form, modelTypes)},
                isLoading,
            }
        ]);

        //eslint-disable-next-line
    }, [isDisabled, isLoading, selectedModels, form, modelTypes]);

    const checkIfLocked = (index: number) => {
        if (navItems[index] === 'Set Prediction Parameters') {
            if (selectedDatasets.length < 1 || selectedModels.length < 1) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    const checkIfComplete = (index: number) => {
        if (selectedDatasets.length > 0 && navItems[index] === 'Select Dataset') {
            return true;
        }

        if (selectedModels.length > 0 && navItems[index] === 'Select Models') {
            return true;
        }

        if (selectedDatasets.length > 0 && selectedModels.length > 0 && navItems[index] === 'Set Prediction Parameters') {
            if (form.name!) {
                return true;
            }
        }

        return false;
    };

    

    return (
        <>
            { isHidden ? (
                null
            ) : (
                <button onClick={handleOpen} className={`w-full px-5 py-3 text-left font-medium flex justify-between items-center gap-3 w-full h-full`}>
                    <div className='flex justify-start items-center gap-3'>
                        <p className='text-nowrap'>{isPrimaryMenu ? 'Batch Prediction' : 'Run Batch Prediction'}</p>
                    </div>
                </button>
            )}

            <ModalBase
                open={open}
                setShowSelect={setShowSelect}
                modalId="batch-pred-popup"
            >
            {!isSuccess ? (
                <div className={`bg-white rounded-lg w-full max-w-[900px] popup-max-height mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'> 
                        <PopupHeader
                            label="Run Batch Prediction"
                            hasOutline={true}
                            closeFn={handleClose}
                        />   
                        <div className="w-full grow flex justify-center items-stretch overflow-y-auto">
                            <PopupSideNav 
                                items={navItems.map((item, i) => ({
                                    label: item,
                                    type: 'number',
                                    isDisabled: checkIfLocked(i),
                                    isActive: selectedNavIndex === i ? true : false,
                                    isComplete: checkIfComplete(i),
                                    action: (e: MouseEvent<HTMLButtonElement>) => {setSelectedNavIndex(i)},
                                }))}
                                selectedIndex={selectedNavIndex}
                            />
                            <div className="w-full grow overflow-y-auto border-l border-outline">
                                {navItems.map((navItem, index) => (
                                    <div className={`w-full p-6 ${index === selectedNavIndex ? 'flex' : 'hidden'} flex-col gap-8 justify-start items-stretch min-h-[600px]`}>
                                        {navItem === 'Select Dataset' ? (
                                            <SelectDatasetV2 
                                                preselectedDatasetId={selectedDatasetId}
                                                selectedDatasets={selectedDatasets}
                                                setSelectedDatasets={setSelectedDatasets}
                                                isPrefetching={isPrefetching}
                                            />
                                        ) : navItem === 'Select Models' ? (
                                            <SelectModelsV2 
                                                preselectedModelFamilyIds={selectedModelFamilyIds}
                                                selectedModels={selectedModels}
                                                setSelectedModels={setSelectedModels}
                                                isPrefetching={isPrefetching}
                                            />
                                        ) : (
                                            <SetPredictionParameters
                                                form={form}
                                                setForm={setForm}
                                                modelTypes={modelTypes}
                                                setModelTypes={setModelTypes}
                                                chosenDS={selectedDatasets.length > 0 ? selectedDatasets[0] : null}
                                                showSelect={showSelect}
                                                numSelectedModels={selectedModels.length}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <PopupFooter 
                            hasOutline={true}
                            buttons={btns}
                        />
                    </div>
                </div>
            ) : (
                <SuccessMessageModal 
                    handleClose={handleClose}
                    message="Your prediction is being generated. You can check its progress in the Jobs Queue. Once the job is complete, the results will appears in the Resultsets page."
                />
            )}
            </ModalBase>
        </>
    )
}